import usePageSEO from '../hooks/usePageSEO';
import { ArticleSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, FileText } from 'lucide-react';
import Footer from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";
const CAL_LINK = "https://cal.com/muhammad-furqan-karim-kidwai-uvsohz/30min";

const guides = [
  {
    title: 'Getting Started with Plouton AI',
    description: 'Set up your first workspace, configure agents, and automate finance workflows in minutes.',
    date: 'March 19, 2026',
    author: 'Plouton AI Team',
    readTime: '8 min read',
    path: '/guides/getting-started',
    tag: 'Guide',
  },
  {
    title: 'A practical guide to AI agents in finance operations',
    description: 'How AI agents are reshaping finance operations — and what your team needs to know to get started.',
    date: 'February 10, 2026',
    author: 'Plouton AI Team',
    readTime: '12 min read',
    path: '/resources',
    tag: 'Ebook',
  },
];

export const GuidesListPage = () => {
  usePageSEO({
    title: 'Guides | Legacy System Automation | Plouton AI',
    description: 'Browse our practical guides to learn about automating your legacy systems. See how our AI agents work within your existing tools without new integration.',
    path: '/guides'
  });

  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-[108px] overflow-hidden">
      <ArticleSchema
        title="Guides | Plouton AI"
        description="Step-by-step guides on implementing AI automation for finance teams."
        url="https://plouton.co/guides"
        datePublished="2026-04-01"
      />
      {/* Hero */}
      <section className="relative py-20 sm:py-28">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/30 via-[#0a0a0f] to-[#0a0a0f]" />
          <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[150px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block">
              Guides
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              Learn how to use Plouton AI
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl">
              Step-by-step guides, best practices, and deep dives to help your team get the most from AI-powered finance automation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guides List */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="space-y-4">
            {guides.map((guide, index) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={guide.path}
                  className="block bg-slate-900/50 border border-slate-800 rounded-xl p-6 sm:p-8 hover:border-slate-700 hover:bg-slate-900/70 transition-all group"
                  data-testid={`guide-card-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      guide.tag === 'Ebook' 
                        ? 'bg-orange-500/15 text-orange-400' 
                        : 'bg-blue-500/15 text-blue-400'
                    }`}>
                      {guide.tag}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      {guide.readTime}
                    </span>
                  </div>

                  <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                    {guide.title}
                  </h2>
                  <p className="text-slate-400 text-sm sm:text-base mb-4">
                    {guide.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-500">
                      {guide.date} · {guide.author}
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidesListPage;
